/**
 * useChatState — all mutable widget state lives here.
 *
 * Keeps the chat panel components pure/presentational: they receive state
 * and dispatch via the returned actions, nothing else.
 *
 * Modes:
 *   - demoMode=true : No Socket.IO. Fake agent reply after 800ms (W2 behaviour,
 *     kept intact for design iteration without a backend).
 *   - demoMode=false: Socket.IO connects lazily on the first panel open.
 *     Messages are sent via the socket; agent replies arrive as socket events.
 *
 * Lazy socket connection: the WS is not opened until the visitor opens the chat
 * panel for the first time — no point keeping a connection alive for visitors
 * who never interact.
 *
 * session_not_ready retry: the backend may emit this error right after the
 * socket connects (agent pipeline initializing). We retry once after 500ms
 * transparently.
 */

import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import type {
  BrandingConfig,
  PreChatFormConfig,
  AvailabilityConfig,
} from '../client/api-types';
import { getAttribution } from '../lib/attribution-store';
import { toMediaAttachment, type MediaAttachment } from '../client/media';
import { parseInteractive, type ReplyButtonOption } from './interactive';
import {
  uploadAttachment as uploadAttachmentRequest,
  type UploadedAttachment,
} from '../client/attachments';
import {
  connectWebchatSocket,
  type WebchatSocketController,
  type ConnectionStatus,
  type OfflineMessageErrorEvent,
} from '../client/socket';
import { evaluateAvailability } from '../lib/availability';
import { capturePageContext } from '../lib/page-context';
import type { VisitorContext } from '../lib/visitor-context';
import { t } from '../lib/i18n';
import {
  createCaptureState,
  start as startCapture,
  submitAnswer,
  skipCurrent,
  canSkipCurrent,
  type CaptureState,
} from './conversationalCapture';

/** Widget-level data-capture strategy, as configured on the widget. */
export type CaptureMode = 'none' | 'pre_chat' | 'inline' | 'conversational';

/**
 * Maximum ms we keep agentTyping=true without a reply before silently resetting.
 * 90s is generous for an LLM (30-60s typical).
 */
const TYPING_TIMEOUT_MS = 90_000;

export type MessageRole = 'user' | 'agent' | 'system';

/**
 * Re-exported so consumers keep importing the option type from the hook
 * that produces it; the type and its parser live in `./interactive`
 * because the history path needs them too.
 */
export type { ReplyButtonOption };

export interface ChatMessage {
  id: string;
  role: MessageRole;
  text: string;
  /** Epoch millis of when the message was sent/received. Optional for backward compat. */
  timestamp?: number;
  /** For demo mode fake replies — purely informational. */
  isDemo?: boolean;
  /**
   * Only populated on `agent` messages. Comes from the `member_type` field
   * on the `agent_message` socket event. Consumers can use this alongside
   * `branding.anonymize_ai_agent` to swap the responder label per bubble
   * (mid-conversation AI→human handoffs stay accurate).
   */
  memberType?: 'ai' | 'human' | null;
  /**
   * Only populated on `agent` messages. Display name of the responder,
   * from the `member_name` field on the socket event. Used to show who is
   * answering in the header subtitle.
   */
  memberName?: string | null;
  /**
   * Only populated on `agent` messages built from an interactive payload
   * (`content.kind === 'interactive'`) — either reply buttons or the rows
   * of a list. Renders as stacked options below the bubble; clicking one
   * sends its `title` as a plain user message.
   */
  replyButtons?: ReplyButtonOption[];
  /**
   * Bold line above the body, from an interactive list's header. Lists are
   * the only payload that carries one. Ignored on plain text messages.
   */
  header?: string;
  /**
   * Optional footer text shown under the bubble body when the interactive
   * payload includes one. Ignored on plain text messages.
   */
  footer?: string;
  /**
   * Attachment on a media message. When set, the bubble renders it above the
   * caption (which travels in `text`, WhatsApp-style). Absent on plain text
   * messages. Built by `toMediaAttachment` — see `client/media.ts`.
   */
  media?: MediaAttachment;
}

/**
 * Latest responder tracked from `agent_message` events. Powers the
 * PanelHeader subtitle so the visitor sees who is answering. `null` until
 * an agent message with member info arrives.
 */
export interface ActiveResponder {
  type: 'ai' | 'human';
  name: string;
}

export interface PreChatData {
  [fieldName: string]: string;
}

/** Counter used for stable, non-crypto IDs (sufficient for local list keys). */
let msgCounter = 0;
function nextId(): string {
  return `msg_${++msgCounter}`;
}

export type AvailabilityStatus = 'online' | 'offline';

/**
 * Lifecycle of the offline contact form's submission. Drives what the widget
 * renders (form / spinner / success screen / error banner) without ever lying
 * to the visitor — we only show "Mensaje enviado" once the backend acks.
 */
export type OfflineFormStatus = 'idle' | 'submitting' | 'sent' | 'error';

export interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  preChatDone: boolean;
  preChatData: PreChatData | null;
  connectionStatus: ConnectionStatus;
  inputDisabled: boolean;
  agentTyping: boolean;
  /**
   * Who last answered on the agent side, per the most recent `agent_message`
   * event that carried member info. Null before the first agent reply, or
   * when all replies came without a resolved member (system messages).
   */
  activeResponder: ActiveResponder | null;
  /**
   * Current availability status. Combines schedule evaluation and real-time
   * agent presence. `'online'` = normal chat; `'offline'` = apply the
   * configured offline behavior (form / message / hidden).
   */
  availabilityStatus: AvailabilityStatus;
  /** Offline form submission lifecycle — see OfflineFormStatus. */
  offlineFormStatus: OfflineFormStatus;
  /** Populated when offlineFormStatus === 'error'. */
  offlineFormError: OfflineMessageErrorEvent | null;
  /**
   * True while conversational capture owns the chat input: the visitor's text
   * is answering a question instead of being sent to the agent.
   */
  captureActive: boolean;
  /** True when the field being asked is optional and can be skipped. */
  captureCanSkip: boolean;
}

export interface ChatActions {
  toggle: () => void;
  open: () => void;
  close: () => void;
  submitPreChat: (data: PreChatData) => void;
  /**
   * @param attachment Receipt from `uploadAttachment`, when the visitor is
   * sending a file. The caption travels in `text` and may be empty.
   */
  sendMessage: (text: string, attachment?: UploadedAttachment) => void;
  /**
   * Uploads a file and returns its receipt. Throws AttachmentUploadError —
   * the composer is expected to surface the reason to the visitor.
   */
  uploadAttachment: (file: File) => Promise<UploadedAttachment>;
  submitOfflineForm: (data: Record<string, string>) => void;
  /** Return to the form after an error, so the visitor can retry. */
  resetOfflineForm: () => void;
  /** Skip the optional field currently being asked. No-op when required. */
  skipCaptureField: () => void;
}

interface UseChatStateOptions {
  branding: BrandingConfig;
  preChatForm: PreChatFormConfig | null;
  /**
   * How visitor data is captured. Only `'conversational'` changes anything
   * here — the other modes are handled entirely by ChatPanel's rendering.
   */
  captureMode?: CaptureMode;
  demoMode?: boolean;
  sessionToken?: string;
  apiBaseUrl?: string;
  /**
   * Optional transcript to seed the chat with — fetched by the caller from
   * the visitor history endpoint after handshake. When non-empty, the pre-
   * chat form is treated as already completed and the greeting is skipped,
   * so we don't duplicate the agent's original hello on top of the history.
   */
  initialMessages?: ChatMessage[];
  ticketRef?: string;
  /** Host-page visitor data, sent once per connection in the socket auth. */
  visitorContext?: VisitorContext;
  /** Availability config from handshake. Absent = always online. */
  availability?: AvailabilityConfig;
  /** Needed to build the upload URL. */
  widgetId?: string;
  visitorId?: string;
}

export function useChatState({
  branding,
  preChatForm,
  captureMode,
  demoMode = false,
  sessionToken,
  apiBaseUrl,
  initialMessages,
  ticketRef,
  visitorContext,
  availability,
  widgetId,
  visitorId,
}: UseChatStateOptions): [ChatState, ChatActions] {
  const hasHistory = !!initialMessages && initialMessages.length > 0;

  const captureFields = preChatForm?.fields ?? [];
  /**
   * Conversational capture needs fields to ask about — an empty list means
   * there is nothing to do, so the widget behaves like `capture_mode: 'none'`.
   * Note it deliberately does NOT look at `preChatForm.enabled`: that flag
   * means "block the chat behind a form", which is the opposite of asking
   * inside the chat. `inline` ignores it for the same reason.
   */
  const conversationalCapture =
    captureMode === 'conversational' && captureFields.length > 0;

  // The greeting is suppressed only for the blocking pre-chat screen.
  // Conversational capture wants the greeting FIRST, then its questions.
  //
  // This keys off the MODE, not off `conversationalCapture`: a conversational
  // widget with `enabled: true` and an empty field list would otherwise be
  // treated as requiring a pre-chat screen that ChatPanel never renders (it
  // only renders one for `capture_mode: 'pre_chat'`), leaving a panel with no
  // greeting and no form at all.
  const requiresPreChat =
    captureMode !== 'conversational' && preChatForm?.enabled === true;

  // Compute initial availability status
  const initialAvailability: AvailabilityStatus = availability
    ? evaluateAvailability(availability)
    : 'online';

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(
    initialMessages ?? [],
  );
  // If we're rehydrating a session, the visitor already completed the
  // pre-chat form (or the widget wasn't configured to require one) — either
  // way, don't show it again on top of a live transcript.
  const [preChatDone, setPreChatDone] = useState(
    (!requiresPreChat && !conversationalCapture) || hasHistory,
  );
  const [preChatData, setPreChatData] = useState<PreChatData | null>(null);
  const [captureState, setCaptureState] = useState<CaptureState>(() =>
    createCaptureState(captureFields, preChatForm?.completion_message),
  );
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected');
  const [inputDisabled, setInputDisabled] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [activeResponder, setActiveResponder] =
    useState<ActiveResponder | null>(null);
  const [availabilityStatus, setAvailabilityStatus] =
    useState<AvailabilityStatus>(initialAvailability);
  const [offlineFormStatus, setOfflineFormStatus] =
    useState<OfflineFormStatus>('idle');
  const [offlineFormError, setOfflineFormError] =
    useState<OfflineMessageErrorEvent | null>(null);

  // Track whether the greeting has been injected yet. Using a ref so we
  // don't need it in the dependency array and avoid double-inject. When we
  // rehydrate a transcript, the original greeting is already in it — mark
  // this true up front so `open()` doesn't inject a fresh one on top.
  const greetingInjected = useRef(hasHistory);

  // Whether the first capture question has been asked. Like `greetingInjected`,
  // a rehydrated transcript counts as already handled: the visitor answered in
  // a previous session and their details are on the contact already.
  const captureStarted = useRef(hasHistory);

  // Socket controller reference — null until first panel open in real mode.
  const socketRef = useRef<WebchatSocketController | null>(null);

  // Store the last sent text to enable session_not_ready retry.
  const lastSentText = useRef<string | null>(null);
  const retryPending = useRef(false);

  // Typing indicator timeout — silently clears after TYPING_TIMEOUT_MS
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref mirror of activeResponder so the socket onAck callback (captured
  // once at connect time) can read the latest responder type without a
  // stale closure.
  const activeResponderRef = useRef<ActiveResponder | null>(null);

  const startTyping = useCallback(() => {
    setAgentTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setAgentTyping(false);
    }, TYPING_TIMEOUT_MS);
  }, []);

  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setAgentTyping(false);
  }, []);

  const addSystemMessage = useCallback((text: string) => {
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'system', text, timestamp: Date.now() },
    ]);
  }, []);

  const connectSocket = useCallback(() => {
    if (demoMode || socketRef.current) return;
    if (!sessionToken || !apiBaseUrl) return;

    socketRef.current = connectWebchatSocket(
      apiBaseUrl,
      sessionToken,
      {
        onStatusChange(status) {
          setConnectionStatus(status);
        },
        onAgentMessage(msg) {
          stopTyping();
          // Prefer the backend timestamp (ISO string). Fall back to now() if the
          // server didn't send one — the bubble needs SOME timestamp to render.
          const ts = msg.timestamp ? Date.parse(msg.timestamp) : Date.now();

          // Options / header / footer only arrive on interactive messages
          // (buttons or a list). Malformed payloads parse to `{}`, so the
          // bubble falls back to plain text instead of throwing.
          const { replyButtons, header, footer } = parseInteractive(
            msg.content,
          );

          // Attachments: the event carries the file descriptor on `content`
          // and the credential at the top level. `apiBaseUrl` is non-null
          // here — connectSocket() bails early without it.
          const media = toMediaAttachment(apiBaseUrl, {
            id: msg.id,
            kind: msg.content?.kind,
            file: msg.content?.file,
            media_token: msg.media_token,
            media_url: msg.media_url,
          });

          setMessages((prev) => [
            ...prev,
            {
              id: nextId(),
              role: 'agent',
              text: msg.content.text,
              timestamp: Number.isFinite(ts) ? ts : Date.now(),
              memberType: msg.member_type ?? null,
              memberName: msg.member_name ?? null,
              ...(replyButtons && replyButtons.length > 0
                ? { replyButtons }
                : {}),
              ...(header ? { header } : {}),
              ...(footer ? { footer } : {}),
              ...(media ? { media } : {}),
            },
          ]);
          // Track "who is answering" for the header subtitle. Only overwrite
          // when the message carries both a type AND a name — a system
          // message (no member) shouldn't wipe the last known responder.
          if (
            (msg.member_type === 'ai' || msg.member_type === 'human') &&
            msg.member_name
          ) {
            const responder = {
              type: msg.member_type,
              name: msg.member_name,
            };
            setActiveResponder(responder);
            activeResponderRef.current = responder;
          }
        },
        onAck(_payload) {
          // Message acknowledged by server. Only show the typing indicator
          // when the last known responder is an AI agent (response comes in
          // seconds) or when we haven't seen any responder yet (optimistic —
          // most webchat widgets are AI-fronted). Once a human agent has
          // answered, stop showing typing on ACK since the human may take
          // minutes to reply and the bouncing dots would be misleading.
          const responder = activeResponderRef.current;
          if (!responder || responder.type === 'ai') {
            startTyping();
          }
        },
        onErrorEvent(err) {
          // Any error clears the typing indicator silently.
          stopTyping();

          if (err.message === 'session_not_ready' && err.retry) {
            // Backend is still initializing; retry once after 500ms.
            if (!retryPending.current && lastSentText.current !== null) {
              retryPending.current = true;
              const textToRetry = lastSentText.current;
              setTimeout(() => {
                retryPending.current = false;
                socketRef.current?.sendUserMessage(textToRetry);
              }, 500);
            }
            return;
          }

          if (err.message === 'rate_limit_exceeded') {
            addSystemMessage(t('rate_limit'));
            return;
          }

          if (err.message === 'budget_exceeded') {
            addSystemMessage(t('budget_exceeded'));
            return;
          }

          addSystemMessage(t('generic_error'));
        },
        onWidgetDisabled(_payload) {
          stopTyping();
          addSystemMessage(t('chat_disabled'));
          setInputDisabled(true);
        },
        onAvailabilityChanged(payload) {
          setAvailabilityStatus(payload.status);
        },
        onOfflineMessageAck() {
          setOfflineFormError(null);
          setOfflineFormStatus('sent');
        },
        onOfflineMessageError(err) {
          setOfflineFormError(err);
          setOfflineFormStatus('error');
        },
      },
      getAttribution(),
      ticketRef,
      capturePageContext(),
      visitorContext,
    );
  }, [
    demoMode,
    sessionToken,
    apiBaseUrl,
    ticketRef,
    visitorContext,
    addSystemMessage,
    startTyping,
    stopTyping,
  ]);

  const injectGreeting = useCallback(() => {
    if (greetingInjected.current) return;
    greetingInjected.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'agent',
        text: branding.greeting,
        timestamp: Date.now(),
      },
    ]);
  }, [branding.greeting]);

  /** Append bot-side capture copy (questions, validation errors) as bubbles. */
  const appendCaptureMessages = useCallback((texts: string[]) => {
    if (texts.length === 0) return;
    setMessages((prev) => [
      ...prev,
      ...texts.map((text) => ({
        id: nextId(),
        role: 'agent' as const,
        text,
        timestamp: Date.now(),
      })),
    ]);
  }, []);

  /**
   * Ask the first capture question, right after the greeting. Guarded by a ref
   * so reopening the panel doesn't re-ask what the visitor already answered.
   */
  const beginCapture = useCallback(() => {
    if (captureStarted.current) return;
    captureStarted.current = true;
    const result = startCapture(captureState);
    setCaptureState(result.state);
    appendCaptureMessages(result.messages);
  }, [captureState, appendCaptureMessages]);

  const open = useCallback(() => {
    setIsOpen(true);
    connectSocket();
    if (!requiresPreChat || preChatDone) {
      injectGreeting();
      if (conversationalCapture) beginCapture();
    }
  }, [
    requiresPreChat,
    preChatDone,
    injectGreeting,
    connectSocket,
    conversationalCapture,
    beginCapture,
  ]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        connectSocket();
        if (!requiresPreChat || preChatDone) {
          injectGreeting();
          if (conversationalCapture) beginCapture();
        }
        return true;
      }
      return false;
    });
  }, [
    requiresPreChat,
    preChatDone,
    injectGreeting,
    connectSocket,
    conversationalCapture,
    beginCapture,
  ]);

  const submitPreChat = useCallback(
    (data: PreChatData) => {
      setPreChatData(data);
      setPreChatDone(true);
      // Forward the captured fields to the backend BEFORE the first message so
      // it creates/enriches the contact (and CRM identity) up front instead of
      // falling back to an anonymous "Visitante". Demo mode has no socket.
      // Same-socket ordering guarantees this lands before any user_message.
      if (!demoMode) {
        // Send-path hygiene: trim values and drop empty (optional) fields.
        // Authoritative validation lives in the backend — this is just to
        // avoid forwarding whitespace-only or blank optional fields.
        const details: PreChatData = {};
        for (const [key, value] of Object.entries(data)) {
          const trimmed = value.trim();
          if (trimmed) details[key] = trimmed;
        }
        socketRef.current?.sendVisitorDetails(details);
      }
      injectGreeting();
    },
    [demoMode, injectGreeting],
  );

  // Signal the backend when the visitor is (probably) leaving so it can
  // shorten its 15s grace period to ~2s. Only fires on `beforeunload`
  // (tab close / navigation away).
  //
  // Previously also fired on `visibilitychange: hidden`, but that
  // triggers on every tab switch, app backgrounding, or panel minimize
  // — producing noisy "El cliente cerró el chat" / "volvió" loops in the
  // Inbox. The backend's default 15s grace period handles real departures
  // fine on mobile (where `beforeunload` is unreliable) — if the socket
  // actually disconnects within 15s the backend still emits `visitor_left`.
  // Skipped in demoMode (no socket) — nothing to signal.
  useEffect(() => {
    if (demoMode) return;

    function signalIntentLeave() {
      socketRef.current?.sendVisitorIntentLeave();
    }

    window.addEventListener('beforeunload', signalIntentLeave);
    return () => {
      window.removeEventListener('beforeunload', signalIntentLeave);
    };
  }, [demoMode]);

  // Re-evaluate schedule-based availability every 60s so the widget
  // transitions at the schedule boundary without a page reload.
  useEffect(() => {
    if (
      !availability ||
      availability.mode === 'always' ||
      availability.mode === 'presence'
    )
      return;
    const timer = setInterval(() => {
      const newStatus = evaluateAvailability(availability);
      setAvailabilityStatus((prev) => {
        if (prev !== newStatus) return newStatus;
        return prev;
      });
    }, 60_000);
    return () => clearInterval(timer);
  }, [availability]);

  const submitOfflineForm = useCallback(
    (data: Record<string, string>) => {
      // Demo mode has no socket — fake the success screen so the UX
      // can be reviewed without a backend.
      if (demoMode) {
        setOfflineFormError(null);
        setOfflineFormStatus('submitting');
        setTimeout(() => setOfflineFormStatus('sent'), 300);
        return;
      }
      // Bail early if the socket isn't wired up (e.g. no session token).
      // Surface as an error instead of silently swallowing the click.
      if (!socketRef.current) {
        setOfflineFormError({
          code: 'not_connected',
          message: 'socket_not_ready',
        });
        setOfflineFormStatus('error');
        return;
      }
      setOfflineFormError(null);
      setOfflineFormStatus('submitting');
      socketRef.current.sendOfflineMessage(data);
    },
    [demoMode],
  );

  const resetOfflineForm = useCallback(() => {
    setOfflineFormError(null);
    setOfflineFormStatus('idle');
  }, []);

  /**
   * Advance the conversational run with the visitor's latest input. Their text
   * still shows as a normal user bubble — from their side this is just chat.
   *
   * On completion the buffered answers go out through `submitPreChat`, i.e. the
   * exact same `visitor_details` path the pre-chat form uses. Nothing is sent
   * before that, so abandoning halfway leaves no partial contact behind.
   */
  const advanceCapture = useCallback(
    (result: ReturnType<typeof submitAnswer>) => {
      setCaptureState(result.state);
      appendCaptureMessages(result.messages);
      if (result.status === 'complete') {
        submitPreChat(result.answers);
      }
    },
    [appendCaptureMessages, submitPreChat],
  );

  const skipCaptureField = useCallback(() => {
    if (!conversationalCapture || preChatDone) return;
    const result = skipCurrent(captureState);
    // Echo the skip so the transcript doesn't jump from question to question
    // with no visible reason.
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'user',
        text: t('capture_skip'),
        timestamp: Date.now(),
      },
    ]);
    advanceCapture(result);
  }, [conversationalCapture, preChatDone, captureState, advanceCapture]);

  /**
   * Object URLs minted for the visitor's own outgoing files, keyed by receipt.
   * Revoked on unmount — without that every file sent leaks a blob for the
   * lifetime of the page.
   */
  const localPreviews = useRef(new Map<string, MediaAttachment>());

  useEffect(
    () => () => {
      for (const preview of localPreviews.current.values()) {
        URL.revokeObjectURL(preview.url);
      }
      localPreviews.current.clear();
    },
    [],
  );

  const uploadAttachment = useCallback(
    async (file: File): Promise<UploadedAttachment> => {
      if (!apiBaseUrl || !sessionToken || !widgetId || !visitorId) {
        throw new Error('upload requires an established session');
      }

      const uploaded = await uploadAttachmentRequest(
        apiBaseUrl,
        widgetId,
        visitorId,
        sessionToken,
        file,
      );

      localPreviews.current.set(uploaded.attachmentToken, {
        kind: uploaded.kind,
        mimeType: file.type || 'application/octet-stream',
        url: URL.createObjectURL(file),
        ...(uploaded.name ? { name: uploaded.name } : {}),
        ...(uploaded.size ? { size: uploaded.size } : {}),
      });

      return uploaded;
    },
    [apiBaseUrl, sessionToken, widgetId, visitorId],
  );

  const sendMessage = useCallback(
    (text: string, attachment?: UploadedAttachment) => {
      // An attachment with no caption is a legitimate message; only a message
      // with neither is nothing.
      if (!text.trim() && !attachment) return;

      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        text,
        timestamp: Date.now(),
        // Echo the visitor's own file immediately from the local blob. The
        // server never sends their message back, and waiting for a round trip
        // to show what they just picked reads as if it were lost.
        ...(attachment && localPreviews.current.get(attachment.attachmentToken)
          ? { media: localPreviews.current.get(attachment.attachmentToken) }
          : {}),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Conversational capture owns the input until every field is resolved.
      // The answer never reaches the socket as a chat message.
      if (conversationalCapture && !preChatDone) {
        advanceCapture(submitAnswer(captureState, text));
        return;
      }

      if (demoMode) {
        // Show typing indicator during the fake reply delay.
        startTyping();
        setTimeout(() => {
          stopTyping();
          const fakeReply: ChatMessage = {
            id: nextId(),
            role: 'agent',
            timestamp: Date.now(),
            text: `Recibí tu mensaje: _"${text}"_\n\n*(Esta es una respuesta demo. En producción, el agente real responde aquí.)*`,
            isDemo: true,
          };
          setMessages((prev) => [...prev, fakeReply]);
        }, 800);
        return;
      }

      // Real mode — send via socket; typing starts on ACK (onAck handler).
      // Capture page context fresh on every send — the visitor may have
      // navigated (SPA) since the socket connected.
      lastSentText.current = text;
      socketRef.current?.sendUserMessage(
        text,
        attachment?.attachmentToken,
        capturePageContext(),
      );
    },
    [
      demoMode,
      startTyping,
      stopTyping,
      conversationalCapture,
      preChatDone,
      captureState,
      advanceCapture,
    ],
  );

  const state: ChatState = {
    isOpen,
    messages,
    preChatDone,
    preChatData,
    connectionStatus,
    inputDisabled,
    agentTyping,
    activeResponder,
    availabilityStatus,
    offlineFormStatus,
    offlineFormError,
    captureActive: conversationalCapture && !preChatDone,
    captureCanSkip:
      conversationalCapture && !preChatDone && canSkipCurrent(captureState),
  };
  const actions: ChatActions = {
    toggle,
    open,
    close,
    submitPreChat,
    sendMessage,
    uploadAttachment,
    submitOfflineForm,
    resetOfflineForm,
    skipCaptureField,
  };

  return [state, actions];
}
